/**
 * Flow Queuing System — the enroll-queue provider's bulk add/remove mutations
 * and their thin call wrappers, extracted verbatim from `EnrollQueueContext.tsx`.
 *
 * These two mutations do NOT wrap `patchMutation` (they hit their own
 * `/bulk` and `/bulk-delete` routes), so — unlike `addMutation`/`patchMutation`/
 * `removeMutation`/`unstickMutation`/`sendMutation`, which stay in the context
 * file reserved for the F1/DBOS worktree — they are safe to move here in
 * full, mutation objects included.
 *
 * Pure move: every body, dependency array and doc comment below is
 * byte-identical to its previous home. The only additions are the hook
 * wrapper, its parameter object, and its return object.
 */
import { useCallback } from 'react';
import { useMutation, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '../../../../lib/api';
import { fetchJson } from '../../../../lib/apiFetchJson';
import { BulkAddResponseSchema, BulkDeleteResponseSchema, type BulkAddResponse, type BulkDeleteResponse } from '@shared/flow/enrollment';
import { QUEUE_KEY } from '../queue-query-keys';
import type { EnrollQueueContextValue } from '../enroll-queue-types';

export interface UseBulkQueueMutationsDeps {
  queryClient: QueryClient;
  invalidateContactState: () => void;
  requestPlayCardLanding: EnrollQueueContextValue['requestPlayCardLanding'];
  openQueueTab: EnrollQueueContextValue['openQueueTab'];
}

export interface BulkQueueMutations {
  bulkAdd: EnrollQueueContextValue['bulkAdd'];
  bulkRemove: EnrollQueueContextValue['bulkRemove'];
}

export function useBulkQueueMutations({
  queryClient,
  invalidateContactState,
  requestPlayCardLanding,
  openQueueTab,
}: UseBulkQueueMutationsDeps): BulkQueueMutations {
  const { apiFetch } = useApi();

  /**
   * `POST /api/flow/queue/items/bulk` (U2's route) — see
   * `EnrollQueueContextValue.bulkAdd`'s doc comment for the full contract.
   * Wire body uses `BulkAddSchema`'s snake_case play fields, the same
   * convention `addMutation` above follows for the single-add wire.
   */
  const bulkAddMutation = useMutation({
    mutationFn: (input: { contactIds: string[]; playTypeId: number; playInstanceRef: string; sourceContext?: string }): Promise<BulkAddResponse> =>
      fetchJson(apiFetch, BulkAddResponseSchema, '/api/flow/queue/items/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactIds: input.contactIds,
          play_type_id: input.playTypeId,
          play_instance_ref: input.playInstanceRef,
          ...(input.sourceContext !== undefined ? { sourceContext: input.sourceContext } : {}),
        }),
      }),
    // Same consume-before-mount race fix `addMutation` above documents:
    // AWAIT the queue invalidation so `bulkAdd`'s landing (forced-open
    // `PlayQueueCard` + banner, right after this resolves) sees the freshly
    // added items already in the cache.
    onSuccess: async () => {
      invalidateContactState();
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });

  /**
   * `POST /api/flow/queue/items/bulk-delete` (D21, flow-contacts-bulk-send
   * QA round-3 plan — the By Play card's "Clear all N"). Validated through
   * `BulkDeleteResponseSchema` (schema-first boundary), matching
   * `bulkAddMutation` above — `bulkRemove` below needs the real per-id
   * outcome array to compute the skipped-count toast.
   *
   * AWAITS the `QUEUE_KEY` invalidation in `onSuccess` — the
   * `addMutation`/`bulkAddMutation`/D23's `removeMutation` pattern, never
   * the fire-and-forget `void` D23 removed from single-item remove. A bulk
   * clear is exactly the kind of write that window mattered for: the card
   * this fired from stays mounted (with its now-stale rows) for the whole
   * refetch round trip otherwise.
   */
  const bulkRemoveMutation = useMutation({
    mutationFn: (itemIds: number[]): Promise<BulkDeleteResponse> =>
      fetchJson(apiFetch, BulkDeleteResponseSchema, '/api/flow/queue/items/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds }),
      }),
    onSuccess: async () => {
      invalidateContactState();
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });

  /**
   * See `EnrollQueueContextValue.bulkAdd`'s doc comment. `requestPlayCardLanding`
   * is called with the RESPONSE's real counts (never the request's requested
   * length — some requested ids may have skipped) — server is authoritative,
   * client-side counts anywhere upstream of this are preview only.
   */
  const bulkAdd = useCallback(
    async (input: { contactIds: string[]; playTypeId: number; playInstanceRef: string; sourceContext?: string }) => {
      const response = await bulkAddMutation.mutateAsync(input);
      const added = response.results.filter(r => r.outcome === 'added').length;
      const skipped = response.results.length - added;
      requestPlayCardLanding({ playTypeId: input.playTypeId, playInstanceRef: input.playInstanceRef, added, skipped, source: 'bulk' });
      openQueueTab();
      return response;
    },
    [bulkAddMutation, requestPlayCardLanding, openQueueTab],
  );

  /**
   * D21 — the By Play card's "Clear all N" (gated behind its own confirm
   * dialog; see `PlayQueueCard`'s `handleConfirmClear`). Surfaces a toast
   * naming the skipped count on a MIXED outcome — a mid-send item survived
   * the clear — rather than a silent partial success; `skipped_not_found`
   * is not surfaced (a benign race: something else already removed that
   * row, exactly the "not-owned is indistinguishable from missing" outcome
   * the single-item DELETE's own 404 already treats as unremarkable).
   */
  const bulkRemove = useCallback(
    async (itemIds: number[]): Promise<void> => {
      // Codex adversarial follow-up (D21 round) — `BulkDeleteSchema` caps at
      // `.max(100)` (matching `SendSchema`'s own cap), but the queue itself
      // has no size limit (`getQueueForOwner`'s `findMany` has no `take`,
      // per D24's own root-cause notes — Spencer's live QA data already had
      // 94-101 item groups). Without chunking, "Clear all" on any group
      // above 100 would 400 the WHOLE request and clear nothing, with no
      // indication why. Chunk into ≤100-id batches, run them SEQUENTIALLY
      // (not in parallel — no reason to hit the server with concurrent
      // batches for one rep action), and aggregate every batch's outcomes
      // into one consolidated toast.
      const CHUNK_SIZE = 100;
      const chunks: number[][] = [];
      for (let i = 0; i < itemIds.length; i += CHUNK_SIZE) {
        chunks.push(itemIds.slice(i, i + CHUNK_SIZE));
      }
      let skippedSending = 0;
      for (const chunk of chunks) {
        const response = await bulkRemoveMutation.mutateAsync(chunk);
        skippedSending += response.results.filter(r => r.outcome === 'skipped_sending').length;
      }
      if (skippedSending > 0) {
        toast.error(`${skippedSending} ${skippedSending === 1 ? 'item is' : 'items are'} still sending — not removed`);
      }
    },
    [bulkRemoveMutation],
  );

  return { bulkAdd, bulkRemove };
}
